/**
 * Returns matching bundle offers based on the selected first option: one
 * per bundle product, using each product's own default variant/content.
 *
 * If `selectedProduct` is given, whichever offer's productType matches it
 * gets swapped to use its actual variant/discount/content instead of the
 * bundle's configured default (e.g. the shopper picked a different firmness
 * for the bundle's pillow).
 */
export function getMatchingBundleOffers(
  pricingMetafield,
  bundleProducts,
  selectedFirstOption,
  selectedProduct
) {
  return bundleProducts
    .map((bundleProduct) =>
      buildBundleOffer(
        bundleProduct,
        pricingMetafield,
        selectedFirstOption,
        selectedProduct
      )
    )
    .filter(Boolean);
}

function buildBundleOffer(
  bundleProduct,
  pricingMetafield,
  selectedFirstOption,
  selectedProduct
) {
  // The bundle product's id - this should never change even when the
  // product shown for this slot is swapped out below.
  const originalBundleProductId = parseInt(
    bundleProduct.shopifyProduct.id.split('/')?.[4]
  );

  const isSlotForSelectedProduct =
    selectedProduct?.shopifyProduct.productType ===
    bundleProduct.shopifyProduct.productType;

  // Use the shopper's selected product for this slot if it matches;
  // otherwise fall back to the bundle's configured default product.
  const shopifyProduct = isSlotForSelectedProduct
    ? selectedProduct.shopifyProduct
    : bundleProduct.shopifyProduct;

  const variant = findMatchingVariant(
    shopifyProduct.variants,
    selectedFirstOption
  );
  if (!variant) return null;

  // Look up this offer's discount for the selected size, always keyed by
  // the bundle's original product id (not the swapped-in one).
  const pricingProduct = pricingMetafield.products.find(
    (p) => p.id.toString() === originalBundleProductId.toString()
  );
  const discount =
    pricingProduct?.variants?.find((v) =>
      findMatchingVariant([v], selectedFirstOption)
    )?.discount || 0;

  // Everything but the shopifyProduct can be reorganized into 'settings'
  const settings = { ...bundleProduct };
  delete settings.shopifyProduct;
  if (isSlotForSelectedProduct) {
    settings.productTabs = selectedProduct.productTabs;
  }

  return {
    variant,
    product: shopifyProduct,
    original_bundle_product_id: originalBundleProductId,
    discount: String(discount),
    settings
  };
}

export function getBundlePriceSummary(
  pricingMetafield,
  currentOffers,
  selectedOptionValue
) {
  let totalDiscountPrice = 0;
  let totalPrice = 0;
  let totalCompareAtPrice = 0;

  // Calculate discount for each offer based on the selected option value
  currentOffers.forEach((offer) => {
    // Find the matching product in pricing metafield
    const pricingProduct = pricingMetafield.products.find(
      (p) => p.id.toString() === offer.original_bundle_product_id.toString()
    );

    if (pricingProduct) {
      // Find the matching variant in pricing metafield for the selected option
      const pricingVariant = pricingProduct.variants.find(
        (v) => extractFirstOption(v) === selectedOptionValue
      );

      if (pricingVariant) {
        const variantPrice = parseFloat(offer.variant.price.amount || 0);
        const variantCompareAtPrice = parseFloat(
          offer.variant.compareAtPrice?.amount || variantPrice
        );
        const metafieldDiscountAmount = parseFloat(
          pricingVariant.discount || 0
        );

        // Check if price >= discount price
        // If discount is greater than price, use price as the effective discount
        const effectiveDiscountAmount =
          metafieldDiscountAmount > variantPrice
            ? variantPrice
            : metafieldDiscountAmount;

        totalDiscountPrice += effectiveDiscountAmount;
        totalPrice += variantPrice;
        totalCompareAtPrice += variantCompareAtPrice;
      } else {
        // If no matching variant in pricing metafield, just add the prices without discount
        const variantPrice = parseFloat(offer.variant.price.amount || 0);
        const variantCompareAtPrice = parseFloat(
          offer.variant.compareAtPrice?.amount || variantPrice
        );

        totalPrice += variantPrice;
        totalCompareAtPrice += variantCompareAtPrice;
      }
    } else {
      // If no matching product in pricing metafield, just add the prices without discount
      const variantPrice = parseFloat(offer.variant.price.amount || 0);
      const variantCompareAtPrice = parseFloat(
        offer.variant.compareAtPrice?.amount || variantPrice
      );

      totalPrice += variantPrice;
      totalCompareAtPrice += variantCompareAtPrice;
    }
  });

  return {
    discountPrice: totalDiscountPrice,
    price: totalPrice - totalDiscountPrice,
    compareAtPrice:
      totalCompareAtPrice > totalPrice ? totalCompareAtPrice : null,
    totalPrice
  };
}

/**
 * @param pricingMetafield Bundle pricing JSON stored in shopify metafield
 * @param products Shopify product data (one per bundle item)
 */
export function getLowestBundlePriceSummary(pricingMetafield, products) {
  const bundleSizes = pricingMetafield.variants.map((variant) => {
    return variant.name;
  });

  // Helper to get the total price and compareAtPrice for a given size
  const getTotalsForSize = (size, products) => {
    let price = 0;
    let compareAtPrice = 0;

    for (const product of products) {
      const variant = product.variants.find((v) =>
        v.selectedOptions.some((o) => o.value === size)
      );
      price = price + parseFloat(variant.price.amount);
      compareAtPrice =
        compareAtPrice + parseFloat(variant.compareAtPrice.amount);
    }

    return { price, compareAtPrice };
  };

  // Get the best price and compareAtPrice from each size
  let best = { price: Infinity, compareAtPrice: Infinity, size: null };
  for (const size of bundleSizes) {
    const totals = getTotalsForSize(size, products);
    if (totals.price < best.price) {
      best = { ...totals, size };
    }
  }

  // Apply discount to the price
  const discount = calculateTotalBundleDiscount(pricingMetafield, best.size);
  const totalPrice = best.price - discount;
  const totalComparePrice =
    best.compareAtPrice > best.price ? best.compareAtPrice : null;
  return {
    discount,
    totalPrice,
    totalComparePrice
  };
}

/**
 * Helper to calculate the total bundle discount based on the given option and
 * bundle product data
 */
const calculateTotalBundleDiscount = (bundlePriceData, option) =>
  bundlePriceData?.products.reduce((total, product) => {
    const discount =
      product.variants.find((variant) => extractFirstOption(variant) === option)
        ?.discount || 0;
    return total + discount;
  }, 0);

/**
 * Helper to extract the first variant option from its title. Normally I would
 * use the `variant.selectedOptions` field which feels much more reliable, but
 * in the case of the metafield used to read bundle pricing info, the variants
 * are modified ones without the `selectedOptions` field.
 */
const extractFirstOption = (variant) => {
  const title = variant.title;
  if (title.includes(' / ')) return title.split(' / ')[0];
  return title;
};

// Helper to find the matching variant for the selected option
export function findMatchingVariant(variants, selectedOptionValue) {
  const normalizedSelectedOption = normalizeTitle(selectedOptionValue);
  return variants.find(
    (variant) => normalizeTitle(variant.title) === normalizedSelectedOption
  );
}

function normalizeTitle(title) {
  return title?.split('/')[0]?.trim().toLowerCase();
}
